import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Webhook, Save, RefreshCw, Send, Loader2, CheckCircle2, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/dashboard/Sidebar";

const WebhooksPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [config, setConfig] = useState({ url: "", secret: "", active: false });
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadDeliveries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/webhooks/deliveries?limit=25`);
      setDeliveries(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/webhooks/config`);
      setConfig({ url: res.data.url || "", secret: res.data.secret || "", active: !!res.data.active });
      await loadDeliveries();
    } catch {
      toast.error("Failed to load webhook settings");
    } finally {
      setLoading(false);
    }
  }, [loadDeliveries]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/webhooks/config`, { url: config.url.trim(), active: config.active });
      setConfig((c) => ({ ...c, url: res.data.url || "", active: !!res.data.active }));
      toast.success("Webhook settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const rotate = async () => {
    if (!window.confirm("Generate a new signing secret? Your other app must be updated with the new value or deliveries will fail verification.")) return;
    try {
      const res = await axios.post(`${API}/webhooks/config/rotate-secret`);
      setConfig((c) => ({ ...c, secret: res.data.secret }));
      toast.success("New secret generated — update your other app");
    } catch {
      toast.error("Failed to rotate secret");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/webhooks/test`);
      toast.success("Test event sent");
      setTimeout(loadDeliveries, 1500);
    } catch {
      toast.error("Failed to send test");
    } finally {
      setTesting(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(config.secret);
    toast.success("Secret copied");
  };

  return (
    <div className="flex min-h-screen bg-zinc-50">
      <Sidebar onLogout={() => { logout(); navigate("/login"); }} />
      <main className="flex-1 p-6 md:p-10 max-w-4xl">
        <div className="mb-6">
          <h1 className="font-heading text-2xl uppercase tracking-tight text-[#00341d] flex items-center gap-2">
            <Webhook className="w-6 h-6" /> Webhooks
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Push quote, customer, and payment changes to your other app automatically as they happen. We POST a signed JSON event to your URL; on failure we retry with backoff and log every attempt below.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <Card className="border border-zinc-200 shadow-sm mb-6">
              <CardContent className="p-6 space-y-5">
                <div>
                  <label className="text-sm font-medium text-zinc-700">Receiver URL</label>
                  <Input
                    value={config.url}
                    onChange={(e) => setConfig((c) => ({ ...c, url: e.target.value }))}
                    placeholder="https://your-other-app.com/webhooks/landclear"
                    className="mt-1"
                    data-testid="webhook-url-input"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-zinc-700">Signing secret</label>
                  <div className="flex gap-2 mt-1">
                    <Input value={config.secret} readOnly className="font-mono text-xs bg-zinc-50" data-testid="webhook-secret-input" />
                    <Button type="button" variant="outline" onClick={copySecret} data-testid="webhook-copy-secret" className="shrink-0"><Copy className="w-4 h-4" /></Button>
                    <Button type="button" variant="outline" onClick={rotate} data-testid="webhook-rotate-secret" className="shrink-0"><RefreshCw className="w-4 h-4 mr-1" /> New</Button>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">We sign each request body with HMAC-SHA256 and send it in the <code>X-LandClear-Signature</code> header (format <code>sha256=&lt;hex&gt;</code>). Verify it with this secret.</p>
                </div>

                <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                  <input type="checkbox" checked={config.active} onChange={(e) => setConfig((c) => ({ ...c, active: e.target.checked }))} data-testid="webhook-active-toggle" className="rounded border-zinc-300 accent-[#00341d]" />
                  <span>Active — send events to this URL</span>
                </label>

                <div className="flex gap-3 pt-1">
                  <Button onClick={save} disabled={saving} data-testid="webhook-save-btn" className="bg-[#00341d] hover:bg-[#00341d]/90">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
                  </Button>
                  <Button onClick={sendTest} disabled={testing || !config.url} variant="outline" data-testid="webhook-test-btn">
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send test event</>}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-sm uppercase tracking-wide text-zinc-500">Recent deliveries</h2>
              <Button variant="ghost" size="sm" onClick={loadDeliveries} data-testid="webhook-refresh-deliveries"><RefreshCw className="w-4 h-4" /></Button>
            </div>
            <Card className="border border-zinc-200 shadow-sm">
              <CardContent className="p-0">
                {deliveries.length === 0 ? (
                  <div className="text-center py-10 text-zinc-400 text-sm" data-testid="webhook-deliveries-empty">No deliveries yet. Save a URL and send a test event.</div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {deliveries.map((d) => (
                      <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`webhook-delivery-${d.id}`}>
                        {d.status === "delivered" ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                        <span className="font-medium text-[#00341d] w-40 truncate">{d.event_type}</span>
                        <span className="text-zinc-400 flex-1 truncate">{(d.created_at || "").replace("T", " ").slice(0, 19)}</span>
                        <span className="text-zinc-500">{d.status_code || "—"}</span>
                        <span className="text-zinc-400 text-xs">{d.attempts} {d.attempts === 1 ? "try" : "tries"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default WebhooksPage;
